import {
  formatAmount,
  formatMoney,
  parseDecimal,
  roundToCurrencyPrecision,
  type FormatAmountOptions
} from '@insio/decimal';
import { Decimal } from 'decimal.js';
import { CurrencyCode, getCurrencyDecimalPlaces } from '@insio/shared-types';
import { useLocale, useNumberFormat } from '../providers';

export interface UseCurrencyPrecisionResult {
  /** Decimal places for the currency, or `null` when no code is provided. */
  decimalPlaces: number | null;
  /**
   * Parse a raw user-entered string, round it to the currency's precision, and
   * return the canonical fixed-decimal representation. Returns `null` when no
   * currency is set or the input cannot be parsed as a decimal.
   */
  format: (raw: string) => string | null;
}

/**
 * Hook: exposes a currency's decimal precision and a `format(raw)` helper that
 * parses + rounds + pads a user-entered string to that precision via
 * `roundToCurrencyPrecision` — avoiding `Number.toFixed`'s FP pitfalls.
 */
export function useCurrencyPrecision(
  currencyCode: CurrencyCode | undefined | null
): UseCurrencyPrecisionResult {
  const decimalPlaces = currencyCode != null ? getCurrencyDecimalPlaces(currencyCode) : null;
  return {
    decimalPlaces,
    format: (raw: string) => {
      if (currencyCode == null || decimalPlaces == null) return null;
      const parsed = parseDecimal(raw);
      if (!parsed) return null;
      return roundToCurrencyPrecision(parsed, currencyCode).toFixed(decimalPlaces);
    }
  };
}

/**
 * Hook: returns a formatter function that formats decimals as currency amounts
 * according to the given currency code and the number format from `UIProvider`.
 * The returned function handles null/undefined/NaN by returning a placeholder
 * instead of a number string.
 * @param currencyCode - the currency code to format amounts with (e.g. 'USD')
 * @return a function that takes a Decimal and returns a formatted string
 */
export function useFormatAmount(
  currencyCode: CurrencyCode | undefined | null
): (amount: Decimal | null | undefined, options?: FormatAmountOptions) => string {
  const numberFormat = useNumberFormat();
  return (amount: Decimal | null | undefined, options?: FormatAmountOptions) => {
    const opts = { currencyCode, numberFormat, ...options };
    return formatAmount(amount, opts);
  };
}

export function useFormatMoney(
  currencyCode: CurrencyCode | undefined | null
): (amount: Decimal | null | undefined, options?: FormatAmountOptions) => string {
  const locale = useLocale();
  return (amount: Decimal | null | undefined, options?: FormatAmountOptions) =>
    formatMoney(amount, { currencyCode, locale, ...options });
}
